// Builders de structured data (JSON-LD) compartilhados entre as páginas.
// Centraliza o schema do site (RealEstateAgent/WebSite), breadcrumb, ItemList e o
// mapeamento de tipo de imóvel, mantendo consistência e @id estáveis entre home,
// busca e página do imóvel.
import { removerAcentos } from './FunctionsGlobals';

const logoFromSite = (site: any): string | undefined => {
  if (site?.url_logo) return `${site.url_logo}?v=${site?.contador_marca || 0}`;
  if (site?.url_icon) return site.url_icon;
  return undefined;
};

// @id estável da imobiliária, usado para ligar entidades (ex.: seller de uma oferta).
export const realEstateAgentId = (baseUrl: string) => `${baseUrl}/#realestateagent`;

// Normaliza uma data para ISO 8601 (yyyy-mm-dd), formato esperado pelo schema.org/Google.
// O backend devolve dd-mm-yyyy (ex.: "18-06-2026"), que o Google marca como inválida.
// Aceita dd-mm-yyyy, dd/mm/yyyy e datas já em ISO; retorna "" se não reconhecer.
export function toIsoDate(value: any): string {
  const raw = String(value || '').trim();
  if (!raw) return '';

  // Já em ISO (yyyy-mm-dd, com ou sem hora) → mantém só a parte da data.
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  // dd-mm-yyyy ou dd/mm/yyyy → yyyy-mm-dd.
  const dmy = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;

  return '';
}

// Mapeia o nome do tipo (PT-BR) para um @type VÁLIDO do schema.org.
// Evita usar o slug ("casa-em-condominio") como @type, que é inválido e gera erro
// no Search Console.
export function mapImovelSchemaType(tipoNome?: string): string {
  const t = removerAcentos(String(tipoNome || '')).toLowerCase();
  if (!t) return 'Accommodation';
  if (/(apart|cobertura|loft|studio|flat|duplex|triplex|garden|jk|kitnet|kitchenette)/.test(t)) {
    return 'Apartment';
  }
  if (/(casa|sobrado|geminad|chacara|sitio|fazenda|rancho)/.test(t)) {
    return 'House';
  }
  return 'Accommodation';
}

// RealEstateAgent — substitui o LocalBusiness genérico; subtipo mais específico para imobiliária.
export function buildRealEstateAgentSchema(site: any, baseUrl: string) {
  const logo = logoFromSite(site);
  const sameAs = [site?.Dados?.facebook, site?.Dados?.instagram, site?.Dados?.youtube].filter(Boolean);
  const hasAddress = Boolean(site?.Dados?.endereco || site?.Dados?.cidade);

  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    '@id': realEstateAgentId(baseUrl),
    name: site?.Dados?.empresa || site?.titulo || 'Imobiliária',
  };
  if (baseUrl) schema.url = baseUrl;
  if (logo) {
    schema.image = logo;
    schema.logo = logo;
  }
  if (site?.Dados?.tel1) schema.telephone = site.Dados.tel1;
  if (hasAddress) {
    schema.address = {
      '@type': 'PostalAddress',
      streetAddress: site?.Dados?.endereco || '',
      addressLocality: site?.Dados?.cidade || '',
      addressRegion: site?.Dados?.estado || '',
      postalCode: site?.Dados?.cep || '',
      addressCountry: 'BR',
    };
  }
  if (sameAs.length) schema.sameAs = sameAs;
  return schema;
}

// WebSite — nome/url do site, ligado ao RealEstateAgent como publisher.
export function buildWebsiteSchema(site: any, baseUrl: string) {
  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${baseUrl}/#website`,
    name: site?.titulo || site?.Dados?.empresa || 'Imobiliária',
  };
  if (baseUrl) {
    schema.url = baseUrl;
    schema.publisher = { '@id': realEstateAgentId(baseUrl) };
  }
  return schema;
}

// BreadcrumbList a partir de uma lista [{ name, item }].
export function buildBreadcrumbSchema(items: Array<{ name: string; item: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: entry.name,
      item: entry.item,
    })),
  };
}

// ItemList de resultados (padrão "summary page"): cada ListItem aponta para a URL
// canônica do item — não embute dados completos por item.
export function buildItemListSchema(elements: Array<{ url: string; name?: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: elements.map((el, index) => {
      const item: Record<string, any> = {
        '@type': 'ListItem',
        position: index + 1,
        url: el.url,
      };
      if (el.name) item.name = el.name;
      return item;
    }),
  };
}

// FAQPage a partir dos pares pergunta/resposta extraídos da descrição rica do
// empreendimento (ver utils/descricaoRica.extraiFaqDaDescricao). Lista vazia →
// `null`, e o chamador NÃO emite o <script> (FAQPage sem mainEntity é erro no
// Rich Results Test e o Google penaliza marcação vazia).
export function buildFaqPageSchema(pares: Array<{ pergunta: string; resposta: string }>) {
  const itens = (Array.isArray(pares) ? pares : []).filter(
    (par) => par?.pergunta?.trim() && par?.resposta?.trim()
  );
  if (!itens.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: itens.map((par) => ({
      '@type': 'Question',
      name: par.pergunta.trim(),
      acceptedAnswer: {
        '@type': 'Answer',
        text: par.resposta.trim(),
      },
    })),
  };
}

// Tipo de schema do EMPREENDIMENTO pelos dados disponíveis: condomínio de
// casas/lotes é `GatedResidenceCommunity`, prédio de apartamentos é
// `ApartmentComplex` (o default histórico da página).
export function mapEmpreendimentoSchemaType(tipoNome?: string, categoriaNome?: string): string {
  const texto = `${String(tipoNome || '')} ${String(categoriaNome || '')}`
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  if (/\b(casa|sobrado|lote|terreno|condominio horizontal|loteamento)\b/.test(texto)) {
    return 'GatedResidenceCommunity';
  }
  return 'ApartmentComplex';
}
